import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, Grid, TextField } from "@mui/material";
import { DialogProps, FormControlLabel } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { DelayCategoryAutocomplete } from "../../../../../shared/autocompletes/DelayCategoryAutocomplete";
import { DelayCodeTreePicker } from "../../../../../shared/treepickers/DelayCodeTreePicker";
import {
  CATALOG_DELAY_INITIAL_VALUES,
  type CatalogDelayCode,
} from "../../models/CatalogDelayCode";
import {
  useDeleteDelayCodeMutation,
  useUpsertDelayCodeMutation,
} from "../../../hooks/mutations/useDelayCodeMutations";
import { useDelayCodeQuery } from "../../../hooks/queries/useDelayCodeQuery";
import { useTheme } from "@mui/material/styles";
import { modalStyles } from "../../styles/modalStyles";
import type { DelayCodeLabels } from "../../delayCodeLabels";

interface Props {
  DelayId: number | null | undefined;
  modal: "create" | "update" | "delete" | "view" | "";
  onHide: () => void;
  delayAreaAssetId?: number;
  delayAreaAssetName?: string;
  delayNodeSelectedInTree: CatalogDelayCode | null;
  allDelayCodes: CatalogDelayCode[];
  labels: DelayCodeLabels;
}

export const UpsertDeleteViewDelay = ({
  DelayId,
  modal,
  onHide,
  delayAreaAssetId,
  delayAreaAssetName,
  delayNodeSelectedInTree,
  allDelayCodes,
  labels,
}: Props) => {
  const theme = useTheme();
  const styles = modalStyles(theme);

  const maxWidth: DialogProps["maxWidth"] = "sm";
  const [error, setError] = useState<string>("");
  const [title, setTitle] = useState<string>("CREATE");
  const [notificationMessage, setNotificationMessage] = useState<string>("");

  const successfullyMessage = `The ${labels.singular.toLowerCase()} was ${modal}d successfully`;

  const isEditing = modal !== "" && modal !== "create";
  const { data: delayCode, isLoading } = useDelayCodeQuery(DelayId, isEditing);
  const isLoadingDelayCode = isEditing && !!DelayId && isLoading;

  const upsertCodeMutation = useUpsertDelayCodeMutation();
  const deleteCodeMutation = useDeleteDelayCodeMutation();

  const { control, reset, setValue, handleSubmit } = useForm<CatalogDelayCode>({
    defaultValues: delayNodeSelectedInTree
      ? delayNodeSelectedInTree
      : CATALOG_DELAY_INITIAL_VALUES,
  });

  const handleCancel = () => {
    onHide();
  };

  const isSubmitLoading =
    upsertCodeMutation.isPending || deleteCodeMutation.isPending;

  const onSubmit: SubmitHandler<CatalogDelayCode> = (
    data: CatalogDelayCode,
  ) => {
    if (modal === "delete") {
      deleteCodeMutation.mutate(
        delayNodeSelectedInTree?.DelayCodeId as number,
        {
          onSuccess: () => {
            setNotificationMessage(successfullyMessage);
            onHide();
          },
          onError: (err) => {
            setError(
              err instanceof Error
                ? err.message
                : `Error deleting ${labels.singular.toLowerCase()}`,
            );
          },
        },
      );
    } else {
      upsertCodeMutation.mutate(data, {
        onSuccess: () => {
          setNotificationMessage(successfullyMessage);
          onHide();
        },
        onError: (err) => {
          setError(
            err instanceof Error
              ? err.message
              : `Error saving ${labels.singular.toLowerCase()}`,
          );
        },
      });
    }
  };

  useEffect(() => {
    if (modal === "") return;

    setTitle(
      modal === "create"
        ? "CREATE"
        : modal === "update"
          ? "EDIT"
          : modal === "delete"
            ? "DELETE"
            : "VIEW",
    );

    if (modal === "create") {
      // If there is a node selected, we take it as parent
      let parentId: number | null = null;
      if (delayNodeSelectedInTree?.ParentDelayCodeId) {
        parentId = delayNodeSelectedInTree.DelayCodeId;
      } else {
        const parentRoot = allDelayCodes.find(
          (node) => node.ParentDelayCodeId === null,
        );
        if (parentRoot) {
          parentId = parentRoot.DelayCodeId;
        }
      }
      reset({
        ...CATALOG_DELAY_INITIAL_VALUES,
        ParentDelayCodeId: parentId,
        DelayCodeName: "",
        DelayCategoryId: null,
        DelayAreaAssetId: delayAreaAssetId!,
      });
    } else if (delayCode) {
      reset({
        ...CATALOG_DELAY_INITIAL_VALUES,
        ParentDelayCodeId: delayCode.ParentDelayCodeId,
        DelayAreaAssetId: delayCode.DelayAreaAssetId,
        DelayCodeId: delayCode.DelayCodeId,
        DelayCodeName: delayCode.DelayCodeName,
        DelayCategoryId: delayCode.DelayCategoryId,
        IsBranch: delayCode.IsBranch,
      });
    } else {
      reset();
    }
  }, [modal, delayCode]);

  return (
    <div>
      <MesfModal
        title={`${title} ${labels.singular.toUpperCase()}`}
        open={modal !== ""}
        maxWidth={maxWidth}
        handleClose={() => handleCancel()}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers>
            {isLoadingDelayCode ? (
              <CenteredLazyLoading />
            ) : (
              <>
                <Grid container spacing={1}>
                  <Grid item md={3} sm={12} xs={10}>
                    <Controller
                      name="DelayAreaAssetId"
                      control={control}
                      render={() => (
                        <>
                          <TextField
                            label="Asset"
                            variant="outlined"
                            value={delayAreaAssetName ?? ""}
                            disabled
                            margin="dense"
                            size="small"
                            fullWidth
                          />
                        </>
                      )}
                    />
                  </Grid>
                  <Grid item md={9} sm={12} xs={10}>
                    <Controller
                      name="DelayCodeName"
                      control={control}
                      rules={{ required: "Name is required" }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          label={`${labels.singular} Name`}
                          variant="outlined"
                          fullWidth
                          margin="dense"
                          autoComplete="off"
                          onChange={onChange}
                          value={value}
                          disabled={modal === "delete" || modal === "view"}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  {(delayNodeSelectedInTree?.ParentDelayCodeId ||
                    modal === "create") && (
                    <Grid item md={12} xs={12}>
                      <Controller
                        name="ParentDelayCodeId"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <DelayCodeTreePicker
                              label="Parent"
                              assetId={delayAreaAssetId ?? null}
                              value={field.value}
                              onChange={field.onChange}
                              fieldError={error}
                              disabled={modal === "delete" || modal === "view"}
                            />
                          </>
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item md={12} xs={12}>
                    <Controller
                      name="DelayCategoryId"
                      control={control}
                      rules={{ required: "Category is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <DelayCategoryAutocomplete
                          label="Category"
                          value={field.value}
                          onChange={field.onChange}
                          fieldError={error}
                          disabled={modal === "delete" || modal === "view"}
                          assetId={delayAreaAssetId ?? null}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <Controller
                      name="IsBranch"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          sx={styles.checkbox}
                          control={
                            <Checkbox
                              checked={field.value as boolean}
                              onChange={(e) => field.onChange(e.target.checked)}
                              name="IsAsset"
                              color="primary"
                              disabled
                            />
                          }
                          label="Has Children"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={handleCancel}
              buttonLabel={modal === "delete" ? "Delete" : "Save"}
              buttonColor={modal === "delete" ? "secondary" : "primary"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <ErrorModal error={error} onHide={() => setError("")} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </div>
  );
};
